import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Bot, User, Send, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';
import { getAiUsage, incrementAiUsage } from '../firebase';
import { auth } from '../firebase';
import { buildCoachContext, buildCoachSystemPrompt, sendCoachMessage } from '../utils/buildCoachContext';
import { calcSaleCommission } from '../utils/commissionCalc';

const quickPrompts = [
  '💬 Give me a word track for price objections',
  '📞 How do I open a phone call?',
  '🎯 Tips for closing today',
  '📊 What are my stats this month?'
];

export default function AIChatbot() {
  const {
    showAI, setShowAI,
    aiMessages, setAiMessages,
    aiInput, setAiInput,
    aiLoading, setAiLoading,
    userData, sales, activities, leads, appointments,
  } = useAppContext();

  const { planConfig, isPro } = usePlan();
  const monthlyLimit = planConfig.aiMessagesPerMonth || 50;

  const [usageCount, setUsageCount] = useState(0);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (showAI) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, showAI]);

  // Load Firestore usage count when modal opens
  useEffect(() => {
    if (!showAI) return;
    const uid = auth.currentUser?.uid;
    if (!uid) { setUsageLoaded(true); return; }

    getAiUsage(uid).then(count => {
      setUsageCount(count);
      setUsageLoaded(true);
    }).catch(() => {
      setUsageLoaded(true);
    });
  }, [showAI]);

  const isAtLimit = usageCount >= monthlyLimit;

  const handleAIChat = useCallback(async (userMessage) => {
    const uid = auth.currentUser?.uid;

    if (usageCount >= monthlyLimit) {
      const upgradeNote = isPro
        ? `Your ${monthlyLimit} message limit resets on the 1st of next month!`
        : `Upgrade to Pro for 200 messages/month →`;
      setAiMessages(prev => [...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: `🚫 You've reached your ${monthlyLimit} message limit this month.\n\n${upgradeNote}` }
      ]);
      setAiInput('');
      return;
    }

    setAiLoading(true);
    const newMessages = [...aiMessages, { role: 'user', content: userMessage }];
    setAiMessages(newMessages);
    setAiInput('');

    // Assemble rawMetrics — scalars only, no arrays, no customer records
    const rawMetrics = {
      repName:        userData?.name ?? 'This Rep',
      reportingMonth: new Date().toLocaleString('default', { month: 'long' }),
      reportingYear:  new Date().getFullYear(),
      unitsSold:      sales?.length ?? 0,
      monthlyGoal:    userData?.monthlyGoal ?? 0,
      commissionMTD:  sales?.reduce((a, s) => a + calcSaleCommission(s, userData), 0) ?? 0,
      totalGrossMTD:  sales?.reduce((a, s) => a + (s.frontGross || 0) + (s.backGross || 0), 0) ?? 0,
      callsMade:      activities?.filter(a => a.type === 'call').length ?? 0,
      textsSent:      activities?.filter(a => a.type === 'text').length ?? 0,
      emailsSent:     activities?.filter(a => a.type === 'email').length ?? 0,
      leadsContacted: leads?.length ?? 0,
      leadsClosed:    sales?.length ?? 0,
      apptSet:        appointments?.filter(a => a.status === 'set').length ?? 0,
      apptShown:      appointments?.filter(a => a.status === 'shown').length ?? 0,
      apptClosed:     appointments?.filter(a => a.status === 'closed').length ?? 0,
      incomeGoal:     userData?.incomeGoal ?? null,
    };

    const coachContext = buildCoachContext({
      currentTab: 'dashboard',
      rawMetrics,
      rawLeadsArray: leads ?? [],
    });

    const chatHistory = newMessages.slice(-10).map(m => ({ role: m.role, content: m.content }));

    try {
      const data = await sendCoachMessage({
        messages: [{ role: 'system', content: buildCoachSystemPrompt() }, ...chatHistory],
        context: coachContext,
      });
      setAiMessages([...newMessages, { role: 'assistant', content: data.content }]);

      // Increment usage in Firestore
      if (uid) {
        await incrementAiUsage(uid);
        setUsageCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('AI Chat error:', error);
      setAiMessages([...newMessages, {
        role: 'assistant',
        content: '⚠️ Having trouble connecting right now. Check your internet connection and try again in a moment!'
      }]);
    }

    setAiLoading(false);
  }, [aiMessages, usageCount, monthlyLimit, isPro, userData, sales, activities, leads, appointments, setAiMessages, setAiInput, setAiLoading]);

  const sendAiMessage = (e) => {
    e.preventDefault();
    if (aiInput.trim()) handleAIChat(aiInput.trim());
  };

  if (!showAI) return null;

  const remainingMsgs = Math.max(0, monthlyLimit - usageCount);

  return (
    <div className="modal-overlay ai-modal-overlay" onClick={() => setShowAI(false)}>
      <div className="ai-chat-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-chat-header">
          <div className="ai-chat-title">
            <Bot size={24} />
            <div>
              <div className="ai-title">AI Sales Assistant</div>
              <div className="ai-subtitle">Your pocket sales coach • {usageLoaded ? `${remainingMsgs} messages left` : '...'}</div>
            </div>
          </div>
          <button className="modal-x" onClick={() => setShowAI(false)}>
            <X size={20} />
          </button>
        </div>

        <div className="ai-messages">
          {aiMessages.length === 0 ? (
            <div className="ai-welcome">
              <Bot size={48} />
              <h3>Hey there! 👋</h3>
              <p>I'm your AI sales coach. I can help with:</p>
              <ul>
                <li>🎯 Sales word tracks &amp; scripts</li>
                <li>💬 Objection handling</li>
                <li>📊 Your sales stats</li>
                <li>🎓 Sales training &amp; tips</li>
              </ul>
              <p>Try one of these:</p>
              <div className="quick-prompts">
                {quickPrompts.map((prompt, i) => (
                  <button key={i} className="quick-prompt"
                    onClick={() => setAiInput(prompt.split(' ').slice(1).join(' '))}
                  >{prompt}</button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {aiMessages.map((msg, i) => (
                <div key={i} className={`ai-message ${msg.role}`}>
                  {msg.role === 'assistant' && <Bot size={20} className="msg-icon" />}
                  <div className="msg-content">{msg.content}</div>
                  {msg.role === 'user' && <User size={20} className="msg-icon" />}
                </div>
              ))}
              {aiLoading && (
                <div className="ai-message assistant">
                  <Bot size={20} className="msg-icon" />
                  <div className="msg-typing"><span></span><span></span><span></span></div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Usage counter */}
        <div style={{
          padding: '6px 16px',
          fontSize: 12,
          color: isAtLimit ? '#ff6b6b' : 'rgba(255,255,255,.35)',
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,.06)',
        }}>
          {usageLoaded
            ? isAtLimit
              ? `You've reached your ${monthlyLimit} message limit this month.${!isPro ? ' Upgrade to Pro for 200 messages/month →' : ' Resets on the 1st.'}`
              : `${usageCount} / ${monthlyLimit} messages used this month`
            : 'Loading usage...'}
        </div>

        <form className="ai-input-form" onSubmit={sendAiMessage}>
          <input
            type="text"
            value={aiInput}
            onChange={e => setAiInput(e.target.value)}
            placeholder={isAtLimit ? 'Message limit reached' : 'Ask me anything...'}
            disabled={aiLoading || isAtLimit}
            className="ai-input"
            autoComplete="off"
            autoCorrect="off"
            enterKeyHint="send"
          />
          <button type="submit" disabled={!aiInput.trim() || aiLoading || isAtLimit} className="ai-send-btn">
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
}

// Floating button exported separately — draggable via long-press
export function AIFab() {
  const { setShowAI } = useAppContext();
  const btnRef = useRef(null);
  const drag = useRef({ active: false, moved: false, startX: 0, startY: 0, lastX: 0, lastY: 0, timer: null });

  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('tracksy_fab_pos'));
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') return saved;
    } catch {}
    return null;
  });

  useEffect(() => {
    if (pos) {
      const s = 64;
      const cx = Math.max(0, Math.min(window.innerWidth - s, pos.x));
      const cy = Math.max(0, Math.min(window.innerHeight - s, pos.y));
      if (cx !== pos.x || cy !== pos.y) {
        const clamped = { x: cx, y: cy };
        setPos(clamped);
        try { localStorage.setItem('tracksy_fab_pos', JSON.stringify(clamped)); } catch {}
      }
    }
  }, []);

  const handleStart = (clientX, clientY) => {
    const d = drag.current;
    d.moved = false; d.active = false;
    d.startX = clientX; d.startY = clientY;
    d.timer = setTimeout(() => {
      d.active = true;
      const btn = btnRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        d.lastX = rect.left; d.lastY = rect.top;
        btn.style.transition = 'none'; btn.style.animation = 'none';
        btn.style.left = rect.left + 'px'; btn.style.top = rect.top + 'px';
        btn.style.bottom = 'auto'; btn.style.right = 'auto';
      }
    }, 500);
  };

  const handleMove = (clientX, clientY) => {
    const d = drag.current;
    if (!d.active) {
      if (Math.abs(clientX - d.startX) > 10 || Math.abs(clientY - d.startY) > 10) clearTimeout(d.timer);
      return;
    }
    d.moved = true;
    const s = 64;
    const x = Math.max(0, Math.min(window.innerWidth - s, clientX - s / 2));
    const y = Math.max(0, Math.min(window.innerHeight - s, clientY - s / 2));
    d.lastX = x; d.lastY = y;
    const btn = btnRef.current;
    if (btn) { btn.style.left = x + 'px'; btn.style.top = y + 'px'; }
  };

  const handleEnd = () => {
    const d = drag.current;
    clearTimeout(d.timer);
    const btn = btnRef.current;
    if (btn) { btn.style.transition = ''; btn.style.animation = ''; }
    if (d.active) {
      if (d.moved) {
        const newPos = { x: d.lastX, y: d.lastY };
        setPos(newPos);
        try { localStorage.setItem('tracksy_fab_pos', JSON.stringify(newPos)); } catch {}
      }
      d.active = false;
      return;
    }
    d.active = false;
    setShowAI(true);
  };

  const onTouchStart = (e) => {
    handleStart(e.touches[0].clientX, e.touches[0].clientY);
    const onTM = (ev) => { if (drag.current.active) ev.preventDefault(); handleMove(ev.touches[0].clientX, ev.touches[0].clientY); };
    const onTE = () => { handleEnd(); window.removeEventListener('touchmove', onTM); window.removeEventListener('touchend', onTE); };
    window.addEventListener('touchmove', onTM, { passive: false });
    window.addEventListener('touchend', onTE);
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
    const onMM = (ev) => handleMove(ev.clientX, ev.clientY);
    const onMU = () => { handleEnd(); window.removeEventListener('mousemove', onMM); window.removeEventListener('mouseup', onMU); };
    window.addEventListener('mousemove', onMM);
    window.addEventListener('mouseup', onMU);
  };

  const style = pos ? { left: pos.x + 'px', top: pos.y + 'px', bottom: 'auto', right: 'auto' } : {};

  return (
    <button ref={btnRef} className="ai-fab" style={style} onTouchStart={onTouchStart} onMouseDown={onMouseDown} title="AI Assistant">
      <Bot size={28} />
    </button>
  );
}
